import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  XOctagon,
  X,
} from "lucide-react";

// Define variants for the alert toast using cva
const alertToastVariants = cva(
  "relative w-full max-w-sm overflow-hidden shadow-lg flex items-start p-4 gap-4",
  {
    variants: {
      variant: {
        success: "",
        warning: "",
        info: "",
        error: "",
      },
      styleVariant: {
        default: "bg-background border",
        filled: "",
      },
    },
    compoundVariants: [
      {
        variant: "success",
        styleVariant: "default",
        className: "text-foreground border-green-200 dark:border-green-700",
      },
      {
        variant: "warning",
        styleVariant: "default",
        className: "text-foreground border-yellow-200 dark:border-yellow-700",
      },
      {
        variant: "info",
        styleVariant: "default",
        className: "text-foreground border-blue-200 dark:border-blue-700",
      },
      {
        variant: "error",
        styleVariant: "default",
        className: "text-foreground border-red-200 dark:border-red-700",
      },
      {
        variant: "success",
        styleVariant: "filled",
        className: "bg-success text-success-foreground",
      },
      {
        variant: "warning",
        styleVariant: "filled",
        className: "bg-warning text-warning-foreground",
      },
      {
        variant: "info",
        styleVariant: "filled",
        className: "bg-info text-info-foreground",
      },
      {
        variant: "error",
        styleVariant: "filled",
        className: "bg-destructive text-destructive-foreground",
      },
    ],
    defaultVariants: {
      variant: "info",
      styleVariant: "default",
    },
  }
);

// Define icon map for different variants
const iconMap = {
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
  error: XOctagon,
};

// Define icon color classes
const iconColorClasses: Record<string, Record<string, string>> = {
  default: {
    success: "text-green-500",
    warning: "text-yellow-500",
    info: "text-blue-500",
    error: "text-red-500",
  },
  filled: {
    success: "text-success-foreground",
    warning: "text-warning-foreground",
    info: "text-info-foreground",
    error: "text-destructive-foreground",
  },
};

// Define progress bar color classes
const progressColorClasses: Record<string, Record<string, string>> = {
  default: {
    success: "bg-green-500",
    warning: "bg-yellow-500",
    info: "bg-blue-500",
    error: "bg-red-500",
  },
  filled: {
    success: "bg-success-foreground/50",
    warning: "bg-warning-foreground/50",
    info: "bg-info-foreground/50",
    error: "bg-destructive-foreground/50",
  },
};

export interface AlertToastProps extends VariantProps<typeof alertToastVariants> {
  /** The title of the alert. */
  title: string;
  /** A more detailed description for the alert. */
  description: string;
  /** A function to call when the alert is dismissed. */
  onClose: () => void;
  /** Duration in milliseconds for the toast to auto-dismiss */
  duration?: number;
  className?: string;
}

const AlertToast = React.forwardRef<HTMLDivElement, AlertToastProps>(
  ({ className, variant = 'info', styleVariant = 'default', title, description, onClose, duration = 5000 }, ref) => {
    const Icon = iconMap[variant!];

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50, scale: 0.3 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
        className={cn(alertToastVariants({ variant, styleVariant }), "pb-0", className)}
      >
        {/* Icon */}
        <div className="flex-shrink-0 pb-4">
          <Icon
            className={cn("h-6 w-6", iconColorClasses[styleVariant!][variant!])}
          />
        </div>

        {/* Content */}
        <div className="flex-1 pb-4">
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-1 text-sm opacity-80">{description}</p>
        </div>
        
        {/* Close Button */}
        <div className="flex-shrink-0 pb-4">
          <button
            onClick={onClose}
            className="inline-flex p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 dark:bg-white/10 overflow-hidden">
          <motion.div
            className={cn("h-full", progressColorClasses[styleVariant!][variant!])}
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: duration / 1000, ease: "linear" }}
          />
        </div>
      </motion.div>
    );
  }
);

AlertToast.displayName = "AlertToast";

export { AlertToast, alertToastVariants };
